/**
 * Survey results export (VER-109) — CSV / JSON / Excel + "export all".
 *
 * CSV and JSON are dependency-free (Blob + anchor download). Excel uses SheetJS
 * (xlsx, pinned to the CDN 0.20.3 build — the npm registry copy is frozen at a
 * CVE-affected 0.18.5; we only WRITE workbooks so the parse-path CVEs don't apply,
 * but we ship the patched build regardless).
 *
 * Mock data in this pass — fed the same CompletedSurvey shape a real endpoint would
 * return, so the export layer needs no change when the API lands.
 */
import * as XLSX from "xlsx";
import type { SurveyDefinition, SurveyResponseValue, SurveyResponseValues } from "../builder/survey.types";
import { allQuestions, effectiveOptions } from "../builder/survey.types";
import { scoreSurvey } from "../builder/scoring";
import { aggregateSurvey } from "./aggregate";
import type { CompletedSurvey } from "./surveyResults.mock";

const RESPONDENT = "Respondent";
const SCORE = "Score";

/** Render a raw answer for a tabular cell (numbers stay numeric; multi → joined labels). */
const cellValue = (
    survey: SurveyDefinition,
    questionId: string,
    value: SurveyResponseValue | undefined,
): string | number => {
    if (value === undefined || value === null || value === "") return "";
    if (Array.isArray(value)) {
        const question = allQuestions(survey).find((q) => q.id === questionId);
        const options = question ? effectiveOptions(survey, question) : [];
        return value.map((v) => options.find((o) => o.value === v)?.label ?? String(v)).join("; ");
    }
    return value; // number or string
};

/** Ordered column headers: Respondent, one per question (its text), then Score if scored. */
const columnHeaders = (survey: SurveyDefinition): string[] => {
    const questionCols = allQuestions(survey).map((q) => q.text || q.id);
    const scored = allQuestions(survey).some((q) => q.scored && q.type !== "freeText");
    return [RESPONDENT, ...questionCols, ...(scored ? [SCORE] : [])];
};

/** One flat row per respondent (keys = column headers). */
export const buildResponseRows = (completed: CompletedSurvey): Record<string, string | number>[] => {
    const { survey, responses } = completed;
    const questions = allQuestions(survey);
    const scored = questions.some((q) => q.scored && q.type !== "freeText");

    return responses.map((response: SurveyResponseValues, index) => {
        const row: Record<string, string | number> = { [RESPONDENT]: index + 1 };
        for (const q of questions) {
            row[q.text || q.id] = cellValue(survey, q.id, response[q.id]);
        }
        if (scored) {
            const result = scoreSurvey(survey, response);
            row[SCORE] = result.finalScore ?? "";
        }
        return row;
    });
};

/** Aggregated distribution rows for the Excel "Summary" sheet. */
const buildSummaryRows = (completed: CompletedSurvey): Record<string, string | number>[] => {
    const analytics = aggregateSurvey(completed.survey, completed.responses);
    const rows: Record<string, string | number>[] = [];
    for (const q of analytics.questions) {
        if (q.distribution) {
            for (const row of q.distribution) {
                rows.push({
                    Question: q.question.text,
                    Option: row.label,
                    Count: row.count,
                    Percent: Math.round(row.pct * 100),
                });
            }
        } else if (q.kind === "vas") {
            rows.push({ Question: q.question.text, Option: "Average", Count: q.average ?? "", Percent: "" });
        } else if (q.kind === "freeText") {
            rows.push({
                Question: q.question.text,
                Option: `${q.textAnswers?.length ?? 0} written answers`,
                Count: "",
                Percent: "",
            });
        }
    }
    return rows;
};

// ── Download helpers (dep-free) ──────────────────────────────────────────────
const triggerDownload = (blob: Blob, filename: string): void => {
    const url = window.URL.createObjectURL(blob);
    try {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
    } finally {
        window.URL.revokeObjectURL(url);
    }
};

const escapeCsv = (v: string | number): string => {
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const slug = (completed: CompletedSurvey): string =>
    (completed.survey.code || completed.survey.title || completed.id).replace(/[^a-z0-9]+/gi, "-").toLowerCase();

/** Excel worksheet/tab names: ≤31 chars, none of : \ / ? * [ ]. */
const sheetName = (survey: SurveyDefinition): string =>
    (survey.code || survey.title || "Survey").replace(/[:\\/?*[\]]/g, " ").slice(0, 31);

// ── Public export functions ──────────────────────────────────────────────────

export const exportSurveyCsv = (completed: CompletedSurvey): void => {
    const headers = columnHeaders(completed.survey);
    const rows = buildResponseRows(completed);
    const lines = [
        headers.map(escapeCsv).join(","),
        ...rows.map((r) => headers.map((h) => escapeCsv(r[h] ?? "")).join(",")),
    ];
    triggerDownload(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }), `${slug(completed)}-results.csv`);
};

export const exportSurveyJson = (completed: CompletedSurvey): void => {
    const payload = {
        survey: {
            code: completed.survey.code,
            title: completed.survey.title,
            higherIsBetter: completed.survey.higherIsBetter,
        },
        programmeName: completed.programmeName,
        completedAt: completed.completedAt,
        respondentCount: completed.responses.length,
        responses: completed.responses,
        analytics: aggregateSurvey(completed.survey, completed.responses),
    };
    triggerDownload(
        new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" }),
        `${slug(completed)}-results.json`,
    );
};

export const exportSurveyXlsx = (completed: CompletedSurvey): void => {
    const workbook = XLSX.utils.book_new();
    const responses = XLSX.utils.json_to_sheet(buildResponseRows(completed), {
        header: columnHeaders(completed.survey),
    });
    XLSX.utils.book_append_sheet(workbook, responses, "Responses");
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(buildSummaryRows(completed)), "Summary");
    XLSX.writeFile(workbook, `${slug(completed)}-results.xlsx`);
};

/** One workbook with a Responses sheet per completed survey. */
export const exportAllXlsx = (all: CompletedSurvey[]): void => {
    const workbook = XLSX.utils.book_new();
    const usedNames = new Set<string>();
    for (const completed of all) {
        let name = sheetName(completed.survey);
        // Excel tab names must be unique — disambiguate collisions.
        let n = 2;
        while (usedNames.has(name)) {
            name = `${sheetName(completed.survey).slice(0, 28)}-${n++}`;
        }
        usedNames.add(name);
        const sheet = XLSX.utils.json_to_sheet(buildResponseRows(completed), {
            header: columnHeaders(completed.survey),
        });
        XLSX.utils.book_append_sheet(workbook, sheet, name);
    }
    XLSX.writeFile(workbook, "all-survey-results.xlsx");
};
